import { createSelector } from '@reduxjs/toolkit';
import { createCachedSelector } from 're-reselect';
import { isEmpty, keyBy, some } from 'lodash/fp';
import { type RootState } from '@/store';
import { type Incident } from '@/types';

export const propertyId = (_: any, { propertyId }: { propertyId: string }) =>
  propertyId;

export const page = (_: any, { page }: { page: number }) => page;

export const incident = (_: any, { incident }: { incident: Incident }) =>
  incident;

export const modalDataIncidentId = (
  _: any,
  { incidentId }: { incidentId: string },
) => incidentId;

export const events = ({ events: { events } }: RootState) => events;
export const pagination = ({ events: { pagination } }: RootState) => pagination;

export const eventTypeOptions = ({ events: { eventTypeOptions } }: RootState) =>
  eventTypeOptions;

export const eventStatusOptions = ({
  events: { eventStatusOptions },
}: RootState) => eventStatusOptions;

export const sportOptions = ({ events: { sportOptions } }: RootState) =>
  sportOptions;

export const currentEvent = ({ events: { currentEvent } }: RootState) =>
  currentEvent;

export const currentIncidents = ({ events: { currentIncidents } }: RootState) =>
  currentIncidents;

export const currentContributors = ({
  events: { currentContributors },
}: RootState) => currentContributors;

export const currentMatchScore = ({
  events: { currentMatchScore },
}: RootState) => currentMatchScore;

export const eventsByPropertyId = createSelector(
  events,
  propertyId,
  (events, propertyId) => events[propertyId],
);

export const eventsByPropertyIdAndPage = createSelector(
  eventsByPropertyId,
  page,
  (events, page) => events?.[page],
);

export const paginationByPropertyId = createSelector(
  pagination,
  propertyId,
  (pagination, propertyId) => pagination?.[propertyId],
);

export const hasEventOptions = createSelector(
  eventTypeOptions,
  eventStatusOptions,
  sportOptions,
  (eventTypeOptions, eventStatusOptions, sportOptions) =>
    !some(isEmpty, [eventTypeOptions, eventStatusOptions, sportOptions]),
);

export const incidentById = createSelector(
  currentIncidents,
  modalDataIncidentId,
  (incidents, incidentId) =>
    incidents.find((incident) => incident.id === Number(incidentId))!,
);

export const currentSportActionsMap = createSelector(
  currentEvent,
  (currentEvent) => keyBy('id', currentEvent?.sport?.actions),
);

export const currentContributorsMap = createSelector(
  currentContributors,
  (currentContributors) => keyBy('user_id', currentContributors),
);

export const currentAction = createCachedSelector(
  incident,
  currentSportActionsMap,
  (incident, currentSportActionsMap) => {
    const { action_id } = incident || {};

    return action_id ? currentSportActionsMap[action_id] : null;
  },
)((_, { incident }) => incident?.id);

export const currentAuthor = createCachedSelector(
  incident,
  currentContributorsMap,
  (incident, currentContributorsMap) => {
    const { author_id } = incident || {};

    return author_id ? currentContributorsMap[author_id] : null;
  },
)((_, { incident }) => incident?.id);
